import axios from 'axios';

const REST_API_URL = process.env.REACT_APP_REST_URL;

// Module types that are not poll votes (aligned with data-service leads export exclusions)
const NON_POLL_MODULE_TYPES = new Set(['product_launch', 'fallback', 'menu']);

const isNonPollModuleType = (row) => NON_POLL_MODULE_TYPES.has(row.module_type);

const isBannerClickRow = (row) => row.module_type === 'banner'
  || row.module_type === 'product_launch'
  || row.module_type === 'fallback'
  || (row.option_title && String(row.option_title).toLowerCase().includes('banner'));

class AnalyticsService {
  constructor() {
    this.baseURL = REST_API_URL;
  }

  // Get analytics data from the leads data export service
  async getAnalyticsData(clientId, startDate, endDate) {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(
        `${this.baseURL}/leads-data-export/statistics/${clientId}`,
        {
          params: {
            start_date: startDate,
            end_date: endDate,
          },
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'application/json',
          },
        },
      );

      return response.data;
    } catch (error) {
      console.error('Error fetching analytics data:', error);
      throw error;
    }
  }

  // Process raw data for TOP Films charts
  static processTopFilmsData(rawData) {
    const votesData = Array.isArray(rawData) ? rawData : (rawData?.data || []);
    if (!votesData || !Array.isArray(votesData)) return { totalVotes: [], averageScore: [] };

    const filteredVotesData = votesData.filter((row) => !isNonPollModuleType(row));

    // Group by module title (film name) and calculate statistics
    const filmStats = {};

    filteredVotesData.forEach((row) => {
      const filmTitle = row.module_title || 'Unknown Film';
      if (!filmStats[filmTitle]) {
        filmStats[filmTitle] = {
          totalVotes: 0,
          totalScore: 0,
          scoreCount: 0,
        };
      }

      filmStats[filmTitle].totalVotes += 1;

      // Invert the scoring: weight 1 (Excellent) = 5 stars, weight 5 (Didn't enjoy) = 1 star
      if (row.option_weight || row.weight) {
        const weight = parseInt(row.option_weight || row.weight, 10);
        if (weight >= 1 && weight <= 5) {
          const invertedScore = 6 - weight; // 1->5, 2->4, 3->3, 4->2, 5->1
          filmStats[filmTitle].totalScore += invertedScore;
          filmStats[filmTitle].scoreCount += 1;
        }
      }
    });

    // Convert to arrays and sort
    const films = Object.entries(filmStats).map(([title, stats]) => ({
      title,
      totalVotes: stats.totalVotes,
      averageScore: stats.scoreCount > 0 ? stats.totalScore / stats.scoreCount : 0,
    }));

    // Top 10 by total votes
    const topByVotes = films
      .sort((a, b) => b.totalVotes - a.totalVotes)
      .slice(0, 10);

    // Top 10 by average score (rating %)
    const topByScore = films
      .filter((film) => film.averageScore > 0)
      .sort((a, b) => b.averageScore - a.averageScore)
      .slice(0, 10);

    return {
      totalVotes: topByVotes,
      averageScore: topByScore,
    };
  }

  // Process raw data for TOP Screenings (individual polls)
  static processTopScreeningsData(rawData) {
    const votesData = Array.isArray(rawData) ? rawData : (rawData?.data || []);
    if (!votesData || !Array.isArray(votesData)) return { totalVotes: [], averageScore: [] };

    const filteredVotesData = votesData.filter((row) => !isNonPollModuleType(row));

    // Group by module_id (individual screening instances)
    const screeningStats = {};

    filteredVotesData.forEach((row) => {
      const moduleId = row.module_id || 'unknown';
      const screeningTitle = `${row.module_title || 'Unknown'} (${row.session_title || 'Session'})`;

      if (!screeningStats[moduleId]) {
        screeningStats[moduleId] = {
          title: screeningTitle,
          totalVotes: 0,
          totalScore: 0,
          scoreCount: 0,
        };
      }

      screeningStats[moduleId].totalVotes += 1;

      // Invert the scoring: weight 1 (Excellent) = 5 stars, weight 5 (Didn't enjoy) = 1 star
      if (row.option_weight || row.weight) {
        const weight = parseInt(row.option_weight || row.weight, 10);
        if (weight >= 1 && weight <= 5) {
          const invertedScore = 6 - weight; // 1->5, 2->4, 3->3, 4->2, 5->1
          screeningStats[moduleId].totalScore += invertedScore;
          screeningStats[moduleId].scoreCount += 1;
        }
      }
    });

    // Convert to arrays and sort
    const screenings = Object.values(screeningStats).map((stats) => ({
      ...stats,
      averageScore: stats.scoreCount > 0 ? stats.totalScore / stats.scoreCount : 0,
    }));

    // Top 10 by total votes
    const topByVotes = screenings
      .sort((a, b) => b.totalVotes - a.totalVotes)
      .slice(0, 10);

    // Top 10 by average score (rating %)
    const topByScore = screenings
      .filter((screening) => screening.averageScore > 0)
      .sort((a, b) => b.averageScore - a.averageScore)
      .slice(0, 10);

    return {
      totalVotes: topByVotes,
      averageScore: topByScore,
    };
  }

  // Process data for Daily User Activity chart
  static processDailyActivityData(rawData) {
    const votesData = Array.isArray(rawData) ? rawData : (rawData?.data || []);
    if (!votesData || !Array.isArray(votesData)) {
      return {
        labels: [],
        dailyVotes: [],
        dailyBannerClicks: [],
        dailyComments: [],
        uniqueUsers: [],
      };
    }

    // Get comments from separate comments array (comments are in a separate table)
    const commentsData = rawData?.comments || [];
    const commentsArray = Array.isArray(commentsData) ? commentsData : [];

    // Group by date
    const dailyStats = {};

    const ensureDay = (date) => {
      if (!dailyStats[date]) {
        dailyStats[date] = {
          voteCount: 0,
          bannerClickCount: 0,
          commentCount: 0,
          uniqueUsers: new Set(),
        };
      }
    };

    // Process votes
    votesData.forEach((row) => {
      const date = new Date(row.timestamp).toISOString().split('T')[0]; // Get YYYY-MM-DD

      ensureDay(date);

      if (!isNonPollModuleType(row)) {
        dailyStats[date].voteCount += 1;
      }

      if (isBannerClickRow(row)) {
        dailyStats[date].bannerClickCount += 1;
      }

      // Use fingerprint (ip_address) or email for unique user identification
      const userId = row.email || row.fingerprint || 'anonymous';
      dailyStats[date].uniqueUsers.add(userId);
    });

    // Process comments
    commentsArray.forEach((comment) => {
      const date = new Date(comment.timestamp).toISOString().split('T')[0]; // Get YYYY-MM-DD

      ensureDay(date);

      dailyStats[date].commentCount += 1;

      // Use email or author for unique user identification
      const userId = comment.email || comment.author || 'anonymous';
      dailyStats[date].uniqueUsers.add(userId);
    });

    // Convert to arrays sorted by date
    const sortedDates = Object.keys(dailyStats).sort();
    const dailyVotes = sortedDates.map((date) => dailyStats[date].voteCount);
    const dailyBannerClicks = sortedDates.map((date) => dailyStats[date].bannerClickCount);
    const dailyComments = sortedDates.map((date) => dailyStats[date].commentCount);
    const uniqueUsers = sortedDates.map((date) => dailyStats[date].uniqueUsers.size);

    return {
      labels: sortedDates,
      dailyVotes,
      dailyBannerClicks,
      dailyComments,
      uniqueUsers,
    };
  }

  // Process data for Total User Activity doughnut
  static processTotalActivityData(rawData) {
    const votesData = Array.isArray(rawData) ? rawData : (rawData?.data || []);
    if (!votesData || !Array.isArray(votesData)) return null;

    // Get comments from separate comments array (comments are in a separate table)
    const commentsData = rawData?.comments || [];
    const commentsPosted = Array.isArray(commentsData) ? commentsData.length : 0;

    let totalVotes = 0;
    const uniqueEmails = new Set();
    let bannerClicks = 0;

    votesData.forEach((row) => {
      // Banner/product_launch/fallback clicks are tracked under "Banner Clicks", not poll votes
      if (!isNonPollModuleType(row)) {
        totalVotes += 1;
      }

      // Count unique emails (check if email field contains @ symbol)
      if (row.email && row.email.includes('@')) {
        uniqueEmails.add(row.email.toLowerCase().trim());
      }

      if (isBannerClickRow(row)) {
        bannerClicks += 1;
      }
    });

    return {
      labels: ['Total Votes', 'Unique Emails', 'Banner Clicks', 'Comments Posted'],
      data: [totalVotes, uniqueEmails.size, bannerClicks, commentsPosted],
    };
  }

  // Process data for Project Overview doughnut
  static processProjectOverviewData(rawData) {
    // Use the new structured data with actual events, sessions, and modules
    if (!rawData || typeof rawData !== 'object') return null;

    const events = rawData.events || [];
    const sessions = rawData.sessions || [];
    const modules = rawData.modules || [];

    // Count modules by type
    const moduleTypes = {};
    modules.forEach((module) => {
      const type = module.type || 'unknown';
      moduleTypes[type] = (moduleTypes[type] || 0) + 1;
    });

    // Create labels and data arrays - start with events and sessions only
    const labels = ['Events', 'Sessions'];
    const data = [events.length, sessions.length];

    // Add module types if we have any
    Object.entries(moduleTypes).forEach(([type, count]) => {
      if (count > 0) {
        labels.push(`${type.charAt(0).toUpperCase() + type.slice(1)} Modules`);
        data.push(count);
      }
    });

    return {
      labels,
      data,
      totalModules: modules.length, // Include total modules count for the title
    };
  }

  // Process data for Popularity per Movie/Module line chart
  static processPopularityTimelineData(rawData) {
    const votesData = Array.isArray(rawData) ? rawData : (rawData?.data || []);
    if (!votesData || !Array.isArray(votesData)) return null;

    const filteredVotesData = votesData.filter((row) => !isNonPollModuleType(row));

    // Group by movie/module and date
    const movieTimelines = {};

    filteredVotesData.forEach((row) => {
      const movieTitle = row.module_title || 'Unknown';
      const date = new Date(row.timestamp).toISOString().split('T')[0];

      if (!movieTimelines[movieTitle]) {
        movieTimelines[movieTitle] = {};
      }

      if (!movieTimelines[movieTitle][date]) {
        movieTimelines[movieTitle][date] = {
          votes: 0,
          totalRating: 0,
          ratingCount: 0,
        };
      }

      movieTimelines[movieTitle][date].votes += 1;

      // Invert the scoring: weight 1 (Excellent) = 5 stars, weight 5 (Didn't enjoy) = 1 star
      if (row.option_weight || row.weight) {
        const weight = parseInt(row.option_weight || row.weight, 10);
        if (weight >= 1 && weight <= 5) {
          const invertedScore = 6 - weight; // 1->5, 2->4, 3->3, 4->2, 5->1
          movieTimelines[movieTitle][date].totalRating += invertedScore;
          movieTimelines[movieTitle][date].ratingCount += 1;
        }
      }
    });

    // Get all unique dates and sort them
    const allDates = new Set();
    Object.values(movieTimelines).forEach((timeline) => {
      Object.keys(timeline).forEach((date) => allDates.add(date));
    });
    const sortedDates = Array.from(allDates).sort();

    // Create datasets for top 5 most active movies
    const movieStats = Object.entries(movieTimelines).map(([title, timeline]) => {
      const totalVotes = Object.values(timeline).reduce((sum, day) => sum + day.votes, 0);
      return { title, timeline, totalVotes };
    });

    const topMovies = movieStats
      .sort((a, b) => b.totalVotes - a.totalVotes)
      .slice(0, 5);

    const datasets = topMovies.map((movie, index) => {
      const colors = [
        'rgba(255, 99, 132, 1)',
        'rgba(54, 162, 235, 1)',
        'rgba(255, 205, 86, 1)',
        'rgba(75, 192, 192, 1)',
        'rgba(153, 102, 255, 1)',
      ];

      const data = sortedDates.map((date) => {
        const dayData = movie.timeline[date];
        return dayData ? dayData.votes : 0;
      });

      return {
        label: movie.title,
        data,
        borderColor: colors[index % colors.length],
        backgroundColor: colors[index % colors.length].replace('1)', '0.1)'),
        tension: 0.1,
        fill: false,
      };
    });

    return {
      labels: sortedDates,
      datasets,
    };
  }
}

export default AnalyticsService;
